import React, { useState, useEffect } from 'react';
import axios from "axios";
import { useLazyQuery } from "@apollo/client";

import { LIST_GPS_DEVICES } from '../../../../../GraphQL/Queries/geolocation';

// import { mockedVehiclesList } from '../tests/mocks/mockData';

export const useGeolocation = (userSession) => {
	const [vehicles, setVehicles] = useState([]);
	const [unrelatedDevices, setUnrelatedDevices] = useState([]);
	const [lastUpdated, setLastUpdated] = useState(new Date());
	const [memoizedAddresses, setMemoizedAddresses] = useState({});
	const [stakeholders, setStakeholders] = useState([]);
	const [vmhs, setVmhs] = useState([]);
	const [selectedVehicle, setSelectedVehicle] = useState(null);
	const [pageLoading, setPageLoading] = useState(true);
	const [nextCursor, setNextCursor] = useState(null);

	const [position, setPosition] = useState([43.7194837, -79.5681195]);
	const [zoom, setZoom] = useState(10);

	const [getGpsDevices, { data, loading, called, networkStatus }] = useLazyQuery(LIST_GPS_DEVICES, {
		variables: {
			stakeholderAndVehicleCenterInputs: userSession?.user?.profile_access?.active_sh_access,
			first: 4,
			filterInput: { withGpsAttached: true },
			vehicleSearchTerm: null,
			after: null,
		},
		notifyOnNetworkStatusChange: true,
		fetchPolicy: "cache-and-network",
	});

	const refreshTimeInSeconds = 10;

	const goToVehicle = vehicle => {
		if (!vehicle?.gpsDevice) return;

		setZoom(12);

		const { latitude, longitude } = vehicle.gpsDevice.deviceStatus;
		setPosition([latitude, longitude]);
	}

	// needed to add stakeholder and vmh objects to each vehicle
	const parseVehicles = (responseVehicles) => responseVehicles.map(async vehicle => {
		const stakeholder = await loadDivisions(vehicle.oemId, vehicle.stakeholderId);
		const vmh = await loadVMHs(vehicle.stakeholderId, vehicle.vehicleManagementHubId);

		return { ...vehicle, stakeholder, vmh };
	});

	const getGeolocationData = (isFirstRender = false) => {
		getGpsDevices().then((response) => {
			const { listVehicles } = response?.data;

			const parsedVehicles = parseVehicles(listVehicles.vehicles);
			setNextCursor(listVehicles.metaData.nextCursor)
			setLastUpdated(new Date());

			Promise.all(parsedVehicles).then((vehiclesList) => {
				setVehicles(vehiclesList);

				if (isFirstRender) {
					setPageLoading(false);
					goToVehicle(vehiclesList[0]);
				}
			});
		}).catch(() => {
			setPageLoading(false);
		});

		// Promise.all(parseVehicles(mockedVehiclesList())).then((parsedVehicles) => {
		// 	setVehicles(parsedVehicles);
		// 	setPageLoading(false);
		// });
	};

	// TODO: get gps devices not related to vehicles from API
	// useEffect(() => {
	// 	setUnrelatedDevices(mockedVehiclesList());
	// }, []);

	const openMapVehiclePin = vehicle => {
		if (!vehicle?.gpsDevice?.deviceStatus) return;

		setSelectedVehicle(vehicle);

		const { latitude, longitude } = vehicle.gpsDevice.deviceStatus;
		setPosition([latitude, longitude]);
		setZoom(16);
	}

	const onPopupClose = () => {
		setSelectedVehicle(null);
	};

	const handleVehicleMarkerClick = (vehicle) => {
		setSelectedVehicle(vehicle);
	};

	// workaround to get address from coordinates if not available from GeoTab API
	const getAddress = (latitude, longitude, setState) => {
		if (!latitude || !longitude) return;

		const memoizeKey = `${latitude}-${longitude}`;
		const memoizedAddress = memoizedAddresses[memoizeKey];

		if (memoizedAddress) {
			setState(memoizedAddress);
			return;
		}

		const reverseAddressUrl = 'https://nominatim.openstreetmap.org/reverse';
		axios.get(`${reverseAddressUrl}?format=jsonv2&lat=${latitude}&lon=${longitude}`)
			.then(({ data: address }) => {
				const { house_number = '', road = '', city = '', town = '', 'ISO3166-2-lvl4': ISO3166 = '', postcode = '' } = address?.address;
				const addressName = address?.name ? `${address?.name}, ` : '';
				const stateCode = ISO3166?.split('-')[1] || '';

				const addressTitle = `${addressName} ${house_number} ${road}, ${city || town}, ${stateCode} ${postcode}`;

				const title = address?.address ? addressTitle : vehicle.displayName;

				setMemoizedAddresses(prevState => ({ ...prevState, [memoizeKey]: title }));

				setState(title);
			}).catch(error => {
				console.error('[getAddress] error: ', error);
			});
	}

	const loadDivisions = async (oemId, stakeholderId) => {
		const memoizedStakeholder = stakeholders[stakeholderId];
		if (memoizedStakeholder) return memoizedStakeholder;

        const response = await axios.post('/ajax/stakeholder/' + oemId + '/options', { filter: 'admin' })

		let vehicleStakeholder;

		response?.data?.stakeholders.forEach(stakeholder => {
			const parsedStakeholder = {
				...stakeholder,
				Division: stakeholder.Division || stakeholder.option,
				SH_ID: stakeholder.id || stakeholder.value,
			};

			setStakeholders(prevState => ({
				...prevState,
				[stakeholder.value]: parsedStakeholder
			}));

			if (stakeholder?.value === stakeholderId) {
				vehicleStakeholder = parsedStakeholder;
			}
		});

		return vehicleStakeholder;
	}

    const loadVMHs = async (stakeholderId, vmhId) => {
		const memoizedVmh = vmhs[vmhId];
		if (memoizedVmh) return memoizedVmh;

        const response = await axios.post('/ajax/vmh/' + stakeholderId + '/options', { filter: 'admin' })

		let vehicleVmh;

		response?.data?.vmhs.forEach(vmh => {
			const parsedVmh = {
				...vmh,
				VMH_ID: vmh.id || vmh.value
			};

			setVmhs(prevState => ({
				...prevState,
				[vmh.value]: parsedVmh
			}));

			if (vmh?.value === vmhId) {
				vehicleVmh = parsedVmh;
			}
		});

		return vehicleVmh;
    }

	// TODO: add unrelated GPS device to a vehicle into our DB
	const addVehicle = (e) => {
		e.stopPropagation();
		console.log('addVehicle');
	}

	return {
		vehicles,
		pageLoading,
		lastUpdated,
		selectedVehicle,
		position,
		zoom,
		openMapVehiclePin,
		getAddress,
		onPopupClose,
		handleVehicleMarkerClick,
		getGeolocationData,
		unrelatedDevices,
		addVehicle,
		refreshTimeInSeconds,
	}

}
